import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { ArrowRight, TrendingUp, Users, Clock, Target, CheckCircle, Award, Calculator, BarChart3, Zap, Shield, Rocket, Star } from 'lucide-react';
import { ROICalculator } from '../components/ROICalculator';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { FAQSection } from '../components/sections/FAQSection';
import { Button } from '../components/ui/button';
import { getApiUrl } from '../utils/apiConfig';
import { Helmet } from 'react-helmet-async';

// Icon map
const iconMap: { [key: string]: any } = {
  Shield,
  Star,
  Award,
  Target,
  CheckCircle2: CheckCircle,
  BarChart3,
  TrendingUp,
  Zap,
  Rocket,
  Calculator,
  Users,
  Clock,
};

export function ROIPage() {
  const [heroData, setHeroData] = useState<any>(null);
  const [ctaData, setCTAData] = useState<any>(null);
  const [benchmarksData, setBenchmarksData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pageSettings, setPageSettings] = useState<any>(null);

  useEffect(() => {
    // Listen for CMS updates
    const handleHeroChange = () => fetchHeroData();
    const handleCTAChange = () => fetchCTAData();
    const handleBenchmarksChange = () => fetchBenchmarksData();

    window.addEventListener('roiHeroChanged', handleHeroChange);
    window.addEventListener('roiCTAChanged', handleCTAChange);
    window.addEventListener('roiBenchmarksChanged', handleBenchmarksChange);

const fetchData = async () => {
  setLoading(true);
  await Promise.all([
    fetchHeroData(),
    fetchCTAData(),
    fetchBenchmarksData(),
    fetch(`${getApiUrl()}/roi-page/settings?t=${Date.now()}`)
      .then(r => r.json())
      .then(r => { if (r.success && r.data) setPageSettings(r.data); })
      .catch(() => {})
  ]);
  setLoading(false);
};

    // Initial fetch
    fetchData();

    return () => {
      window.removeEventListener('roiHeroChanged', handleHeroChange);
      window.removeEventListener('roiCTAChanged', handleCTAChange);
      window.removeEventListener('roiBenchmarksChanged', handleBenchmarksChange);
    };
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchHeroData(),
      fetchCTAData(),
      fetchBenchmarksData()
    ]);
    setLoading(false);
  };

  const fetchHeroData = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/roi-page/hero?t=${Date.now()}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setHeroData(result.data);
        } else {
          // Set default data if API returns no data
          setDefaultHeroData();
        }
      } else {
        // Set default data on error
        setDefaultHeroData();
      }
    } catch (error) {
      setDefaultHeroData();
    }
  };

  const fetchCTAData = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/roi-page/cta?t=${Date.now()}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setCTAData(result.data);
        } else {
          setDefaultCTAData();
        }
      } else {
        setDefaultCTAData();
      }
    } catch (error) {
      setDefaultCTAData();
    }
  };

  const fetchBenchmarksData = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/roi-page/benchmarks?t=${Date.now()}`);
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setBenchmarksData(result.data);
        } else {
          setDefaultBenchmarksData();
        }
      } else {
        setDefaultBenchmarksData();
      }
    } catch (error) {
      setDefaultBenchmarksData();
    }
  };

  const setDefaultHeroData = () => {
    setHeroData({
      badge: { text: 'CFO-Approved ROI Model', icon: 'Shield' },
      headline: 'Forecast the Financial Impact of 3D Animation',
      subhead: 'Used by 280+ executives to justify animation budgets with data-driven projections',
      trustPills: [
        { icon: 'BarChart3', text: 'Data-Driven Insights', visible: true },
        { icon: 'Target', text: 'Industry Benchmarks', visible: true },
        { icon: 'Shield', text: 'Board-Ready Reports', visible: true },
        { icon: 'Award', text: 'CFO-Approved Metrics', visible: true }
      ]
    });
  };

  const setDefaultCTAData = () => {
    setCTAData({
      badge: { text: 'Ready to Achieve This ROI?', icon: 'Calculator' },
      headline: 'Get a Custom Proposal Based on Your Results',
      description: 'Our team will create a personalized video strategy to help you achieve these exact outcomes.',
      primaryButton: { text: 'Get Your Custom Proposal', link: '/contact' },
      secondaryButton: { text: 'View Case Studies', link: '/portfolio' },
      trustPills: [
        { icon: 'CheckCircle', text: '24hr Response Time', visible: true },
        { icon: 'Shield', text: 'No Long-Term Contracts', visible: true },
        { icon: 'Award', text: 'Fortune 500 Trusted', visible: true }
      ]
    });
  };

  const setDefaultBenchmarksData = () => {
    setBenchmarksData({
      badge: { text: 'Industry Benchmarks', icon: 'BarChart3' },
      headline: 'How Your Results Compare to Industry Standards',
      description: 'Data from 280+ implementations across manufacturing, energy, and automotive sectors.',
      primaryStats: [
        {
          stat: '40-60%',
          title: 'Reduce Training Costs',
          description: 'Less time to proficiency',
          icon: 'Users',
          gradient: 'from-[#2F2E5C] to-[#4a487a]'
        },
        {
          stat: '25-40%',
          title: 'Increase Sales Conversions',
          description: 'Better product visualization',
          icon: 'TrendingUp',
          gradient: 'from-[#D74444] to-[#b93838]'
        },
        {
          stat: '35-50%',
          title: 'Reduce Support Tickets',
          description: 'Self-service clarity',
          icon: 'Target',
          gradient: 'from-[#2F2E5C] to-[#4a487a]'
        },
        {
          stat: '8-12mo',
          title: 'Typical Payback Period',
          description: 'Industry average ROI timeline',
          icon: 'CheckCircle',
          gradient: 'from-[#D74444] to-[#b93838]'
        }
      ],
      secondaryStats: [
        { value: '3-5x', label: 'Better Engagement', icon: 'Zap' },
        { value: '95%+', label: 'Information Retention', icon: 'Award' },
        { value: '60%', label: 'Faster Onboarding', icon: 'Clock' }
      ]
    });
  };

  if (loading) {
    return (
      <div className="w-full bg-gradient-to-br from-gray-50 via-white to-gray-100">
        {/* Skeleton Hero Section */}
        <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              {/* Skeleton Badge */}
              <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-3 py-1.5 rounded-full mb-4 border border-white/40 shadow-lg animate-pulse">
                <div className="w-3.5 h-3.5 bg-gray-300 rounded-full" />
                <div className="h-3 w-32 bg-gray-300 rounded" />
              </div>
              
              {/* Skeleton Headline */}
              <div className="space-y-3 mb-3">
                <div className="h-8 sm:h-10 lg:h-12 w-full max-w-2xl bg-gray-200 rounded-lg mx-auto animate-pulse" />
                <div className="h-8 sm:h-10 lg:h-12 w-full max-w-xl bg-gray-200 rounded-lg mx-auto animate-pulse" />
              </div>
              
              {/* Skeleton Subhead */}
              <div className="space-y-2 mb-8 max-w-3xl mx-auto">
                <div className="h-4 w-full bg-gray-100 rounded mx-auto animate-pulse" />
                <div className="h-4 w-3/4 bg-gray-100 rounded mx-auto animate-pulse" />
              </div>

              {/* Skeleton Trust Pills */}
              <div className="flex flex-wrap items-center justify-center gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl px-4 py-2.5 rounded-full border border-white/60 shadow-md animate-pulse">
                    <div className="w-4 h-4 bg-gray-300 rounded-full" />
                    <div className="h-3 w-24 bg-gray-300 rounded" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Skeleton Calculator Section */}
        <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-xl border border-gray-200">
              {/* Skeleton Calculator Header */}
              <div className="text-center mb-10">
                <div className="h-8 w-64 bg-gray-200 rounded-lg mx-auto mb-4 animate-pulse" />
                <div className="h-4 w-96 max-w-full bg-gray-100 rounded mx-auto animate-pulse" />
              </div>

              {/* Skeleton Calculator Form */}
              <div className="grid lg:grid-cols-2 gap-8 mb-8">
                {/* Left Column - Inputs */}
                <div className="space-y-6">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                      <div className="h-5 w-40 bg-gray-200 rounded mb-3 animate-pulse" />
                      <div className="h-12 w-full bg-gray-100 rounded-lg animate-pulse" />
                    </div>
                  ))}
                </div>

                {/* Right Column - Results */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200">
                  <div className="h-6 w-32 bg-gray-200 rounded mb-6 animate-pulse" />
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-white rounded-xl p-4 border border-gray-100">
                        <div className="h-4 w-28 bg-gray-100 rounded mb-2 animate-pulse" />
                        <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Skeleton Benchmarks Section */}
        <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white">
          <div className="max-w-6xl mx-auto">
            {/* Skeleton Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg animate-pulse">
                <div className="w-4 h-4 bg-gray-300 rounded-full" />
                <div className="h-3 w-32 bg-gray-300 rounded" />
              </div>
              <div className="h-8 w-96 max-w-full bg-gray-200 rounded-lg mx-auto mb-4 animate-pulse" />
              <div className="h-4 w-full max-w-2xl bg-gray-100 rounded mx-auto animate-pulse" />
            </div>

            {/* Skeleton Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-md border border-white/60">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl mb-3 animate-pulse" />
                  <div className="h-10 w-20 bg-gray-200 rounded-lg mb-2 animate-pulse" />
                  <div className="h-4 w-32 bg-gray-100 rounded mb-1 animate-pulse" />
                  <div className="h-3 w-24 bg-gray-50 rounded animate-pulse" />
                </div>
              ))}
            </div>

            {/* Skeleton Pills */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-xl px-5 py-3 rounded-full border border-white/60 shadow-md animate-pulse">
                  <div className="w-5 h-5 bg-gray-300 rounded-full" />
                  <div className="h-4 w-32 bg-gray-300 rounded" />
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Get icon components
  const HeroBadgeIcon = heroData?.badge?.icon ? iconMap[heroData.badge.icon] || Shield : Shield;
  const CTABadgeIcon = ctaData?.badge?.icon ? iconMap[ctaData.badge.icon] || Calculator : Calculator;
  const BenchmarksBadgeIcon = benchmarksData?.badge?.icon ? iconMap[benchmarksData.badge.icon] || BarChart3 : BarChart3;

  return (
    <>
    <Helmet>
      <title>{pageSettings?.seo?.metaTitle || 'ROI Calculator | Creabiz Studio'}</title>
      <meta name="description" content={pageSettings?.seo?.metaDescription || 'Calculate the financial impact of 3D animation for your business. Used by 280+ executives to justify animation budgets.'} />
      <meta property="og:title" content={pageSettings?.seo?.ogTitle || pageSettings?.seo?.metaTitle || 'ROI Calculator | Creabiz Studio'} />
      <meta property="og:description" content={pageSettings?.seo?.ogDescription || pageSettings?.seo?.metaDescription || 'Calculate the ROI of 3D animation for your business.'} />
      {pageSettings?.seo?.ogImage && <meta property="og:image" content={pageSettings.seo.ogImage} />}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={pageSettings?.seo?.ogTitle || pageSettings?.seo?.metaTitle || 'ROI Calculator | Creabiz Studio'} />
      <meta property="twitter:description" content={pageSettings?.seo?.ogDescription || pageSettings?.seo?.metaDescription || 'Calculate the ROI of 3D animation.'} />
    </Helmet>
      {/* SECTION 1: Combined Hero + Trust Pills */}
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Subtle Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          {/* Content Container */}
          <div className="text-center mb-12">
            {/* Premium Badge */}
            <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-3 py-1.5 rounded-full mb-4 border border-white/40 shadow-lg">
              <HeroBadgeIcon className="w-3.5 h-3.5 text-[#E87D3E]" />
              <span className="text-xs font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                {heroData?.badge?.text || 'CFO-Approved ROI Model'}
              </span>
            </div>
            
            {/* Headline */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 leading-tight text-[#2F2E5C]">
              {heroData?.headline || 'Forecast the Financial Impact of 3D Animation'}
            </h1>
            
            {/* Subhead */}
            <p className="text-sm sm:text-base text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              {heroData?.subhead || 'Used by 280+ executives to justify animation budgets with data-driven projections'}
            </p>

            {/* Trust Pills - 4 Badges */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              {heroData?.trustPills?.filter((p: any) => p.visible !== false).map((item: any, i: number) => {
                const PillIcon = iconMap[item.icon] || BarChart3;
                return (
                  <div key={i} className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-xl px-4 py-2.5 rounded-full border border-white/60 shadow-md hover:shadow-lg transition-all group">
                    <PillIcon className="w-4 h-4 text-[#E87D3E] group-hover:scale-110 transition-transform" />
                    <span className="text-xs font-medium text-[#2F2E5C]">{item.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Interactive ROI Calculator */}
      <section id="roi-calculator" className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
        {/* Subtle Background Gradients */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-[#E87D3E] to-[#E8B84D] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <ROICalculator />
        </div>
      </section>

      {/* SECTION 3: Final CTA - Get Custom Proposal */}
      <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#2F2E5C] via-[#4A567B] to-[#2F2E5C] overflow-hidden">
        {/* Animated Background Orbs */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#E8B84D] via-[#E87D3E] to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '8s' }} />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#E87D3E] to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '6s' }} />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-xl px-4 py-2 rounded-full mb-6 border border-white/20 shadow-lg">
            <CTABadgeIcon className="w-4 h-4 text-[#E8B84D]" />
            <span className="text-sm font-semibold text-white/90">
              {ctaData?.badge?.text || 'Ready to Achieve This ROI?'}
            </span>
          </div>

          {/* Headline */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white leading-tight">
            {ctaData?.headline || 'Get a Custom Proposal Based on Your Results'}
          </h2>

          {/* Description */}
          <p className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
            {ctaData?.description || 'Our team will create a personalized video strategy to help you achieve these exact outcomes.'}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ctaData?.primaryButton?.link || '/contact'}>
              <Button className="group bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full px-8 h-14 gap-2 shadow-xl shadow-[#E87D3E]/40 font-bold text-lg">
                {ctaData?.primaryButton?.text || 'Get Your Custom Proposal'}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to={ctaData?.secondaryButton?.link || '/portfolio'}>
              <Button className="bg-white/10 hover:bg-white/20 backdrop-blur-xl text-white border-2 border-white/30 rounded-full px-8 h-14 gap-2 shadow-lg font-bold text-lg">
                {ctaData?.secondaryButton?.text || 'View Case Studies'}
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>

          {/* Trust Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-8 text-sm">
            {ctaData?.trustPills?.filter((p: any) => p.visible !== false).map((item: any, i: number) => {
              const PillIcon = iconMap[item.icon] || CheckCircle;
              return (
                <div key={i} className="inline-flex items-center gap-2 text-white/70">
                  <PillIcon className="w-4 h-4" />
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4: Social Proof - Executive Testimonials */}
      <TestimonialsSection />

      {/* SECTION 5: Industry Benchmarks (Results Validation) */}
      <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
        {/* Subtle Background Gradients */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] rounded-full blur-3xl" />
          <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-gradient-to-l from-[#E87D3E] to-[#E8B84D] rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
              <BenchmarksBadgeIcon className="w-4 h-4 text-[#E87D3E]" />
              <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                {benchmarksData?.badge?.text || 'Industry Benchmarks'}
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
              {benchmarksData?.headline || 'How Your Results Compare to Industry Standards'}
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {benchmarksData?.description || 'Data from 280+ implementations across manufacturing, energy, and automotive sectors.'}
            </p>
          </div>

          {/* Primary Stats Grid - Premium Frosted Glass Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {benchmarksData?.primaryStats?.filter((s: any) => s.visible !== false).map((item: any, i: number) => {
              const StatIcon = iconMap[item.icon] || Users;
              return (
                <div key={i} className="group relative bg-white/70 backdrop-blur-xl rounded-2xl p-5 shadow-md border border-white/60 hover:shadow-xl hover:-translate-y-1 transition-all">
                  {/* Gradient Icon Circle */}
                  <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mb-3 text-white group-hover:scale-110 transition-transform shadow-lg`}>
                    <StatIcon className="w-5 h-5" />
                  </div>
                  
                  {/* Stat */}
                  <div className={`text-3xl font-bold mb-2 bg-gradient-to-r ${item.gradient} bg-clip-text text-transparent`}>
                    {item.stat}
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-sm font-bold mb-1 text-[#2F2E5C]">{item.title}</h3>
                  
                  {/* Description */}
                  <p className="text-xs text-gray-600">{item.description}</p>
                </div>
              );
            })}
          </div>

          {/* Secondary Stats - Horizontal Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            {benchmarksData?.secondaryStats?.filter((s: any) => s.visible !== false).map((item: any, i: number) => {
              const StatIcon = iconMap[item.icon] || Zap;
              return (
                <div key={i} className="inline-flex items-center gap-3 bg-white/80 backdrop-blur-xl px-5 py-3 rounded-full border border-white/60 shadow-md hover:shadow-lg transition-all group">
                  <StatIcon className="w-5 h-5 text-[#E87D3E] group-hover:scale-110 transition-transform" />
                  <div className="text-sm">
                    <span className="font-bold text-[#2F2E5C]">{item.value}</span>
                    <span className="text-gray-600 ml-1">{item.label}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 6: FAQs */}
      <FAQSection />
    </>
  );
}